import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  FiHome,
  FiUserPlus,
  FiUsers,
  FiMessageSquare,
  FiBarChart2,
  FiSend,
  FiUser,
  FiFlag,
  FiShield,
  FiX
} from "react-icons/fi";

const AdminSidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasPermission, isSuperAdmin, hasAnyPermission } = useAuth();

  // Get the correct dashboard path based on user role
  const getDashboardPath = () => {
    if (user?.role === 'state-admin') {
      return "/admin/state-dashboard";
    } else if (user?.role === 'sd-admin') {
      return "/admin/senatorial-dashboard";
    } else if (user?.role === 'fc-admin') {
      return "/admin/federal-constituency-dashboard";
    } else if (user?.role === 'lga-admin') {
      return "/admin/lga-dashboard";
    } else if (user?.role === 'lcda-admin') {
      return "/admin/lcda-dashboard";
    } else if (user?.role === 'ward-admin') {
      return "/admin/ward-dashboard";
    } else if (user?.role === 'polling-unit-admin') {
      return "/admin/polling-unit-dashboard";
    } else {
      return "/admin/dashboard";
    }
  };

  // Check if current path is active
  const isActivePath = (path) => {
    if (path === "/admin/dashboard") {
      return location.pathname.includes("dashboard");
    }
    return location.pathname === path;
  };

  // Check if admin can create admins below them
  const canCreateAdmins = () => {
    if (isSuperAdmin) return true;
    if (user?.role === 'state-admin') return false;
    if (user?.role === 'sd-admin') {
      return hasAnyPermission(["create-fc-admin", "create-lga-admin", "create-lcda-admin"]);
    }
    if (user?.role === 'fc-admin') {
      return hasAnyPermission(["create-lcda-admin", "create-ward-admin"]);
    }
    if (user?.role === 'lga-admin') return false;
    if (user?.role === 'lcda-admin') return hasPermission("create-ward-admin");
    if (user?.role === 'ward-admin') return hasPermission("create-polling-unit-admin");
    return false;
  };

  // Check if admin can view other admins
  const canViewAdmins = () => {
    if (isSuperAdmin) return true;
    if (user?.role === 'state-admin') return true;
    if (user?.role === 'sd-admin') {
      return hasAnyPermission(["create-fc-admin", "create-lga-admin", "create-lcda-admin"]);
    }
    if (user?.role === 'fc-admin') {
      return hasAnyPermission(["create-lcda-admin", "create-ward-admin"]);
    }
    if (user?.role === 'lga-admin') return false;
    if (["lcda-admin", "ward-admin"].includes(user?.role)) return canCreateAdmins();
    return false;
  };

  // Check if admin can manage members
  const canManageMembers = () => {
    if (user?.role === 'lga-admin') {
      return hasAnyPermission([
        "view-members", "create-members", "edit-members", "delete-members",
        "approve-members", "suspend-members", "reject-members", "reactivate-members"
      ]);
    }
    return hasAnyPermission([
      "view-members", "create-members", "edit-members", "delete-members",
      "approve-members", "suspend-members", "reject-members", "reactivate-members"
    ]);
  };

  // Check if admin can broadcast
  const canBroadcast = () => {
    if (user?.role === 'lga-admin') {
      return hasAnyPermission([
        "broadcast-announcement-local-government",
        "broadcast-announcement-lcda",
        "broadcast-announcement-ward",
        "broadcast-announcement-polling-unit",
        "broadcast-request-local-government",
        "broadcast-request-lcda",
        "broadcast-request-ward",
        "broadcast-request-polling-unit"
      ]);
    }
    return hasAnyPermission([
      "broadcast-announcement-nationwide",
      "broadcast-announcement-state",
      "broadcast-announcement-senatorial-district",
      "broadcast-announcement-federal-constituency",
      "broadcast-announcement-local-government",
      "broadcast-announcement-lcda",
      "broadcast-announcement-ward",
      "broadcast-announcement-polling-unit",
      "broadcast-request-nationwide",
      "broadcast-request-state",
      "broadcast-request-senatorial-district",
      "broadcast-request-federal-constituency",
      "broadcast-request-local-government",
      "broadcast-request-lcda",
      "broadcast-request-ward",
      "broadcast-request-polling-unit"
    ]);
  };

  // Check if admin can create group chats
  const canCreateGroupChats = () => {
    if (user?.role === 'lga-admin') {
      return hasPermission("create-group-chat-local-government");
    }
    return hasAnyPermission([
      "create-group-chat-nationwide",
      "create-group-chat-state",
      "create-group-chat-senatorial-district",
      "create-group-chat-federal-constituency",
      "create-group-chat-local-government",
      "create-group-chat-lcda",
      "create-group-chat-ward",
      "create-group-chat-polling-unit"
    ]);
  };

  // Check if admin can view reports
  const canViewReports = () => {
    if (user?.role === 'lga-admin') {
      return hasAnyPermission([
        "view-reports",
        "view-lga-reports",
        "view-lcda-reports",
        "view-ward-reports",
        "view-polling-unit-reports"
      ]);
    }
    return hasAnyPermission([
      "view-reports",
      "view-nationwide-reports",
      "view-state-reports",
      "view-sd-reports",
      "view-fc-reports",
      "view-lga-reports",
      "view-lcda-reports",
      "view-ward-reports",
      "view-polling-unit-reports"
    ]);
  };

  // Get menu items based on permissions
  const getMenuItems = () => {
    const dashboardPath = getDashboardPath();
    
    const items = [
      { 
        name: "Dashboard", 
        path: dashboardPath, 
        icon: <FiHome />,
        allowed: true
      },
      {
        name: "Profile",
        path: "/admin/profile",
        icon: <FiUser />,
        allowed: true
      },
      {
        name: "View Members",
        path: "/admin/view-members",
        icon: <FiUsers />,
        allowed: canManageMembers()
      },
      {
        name: "Permissions",
        path: "/admin/create-admin",
        icon: <FiUserPlus />,
        allowed: canCreateAdmins() && user?.role !== 'lga-admin'
      },
      {
        name: "View Admins",
        path: "/admin/view-admins",
        icon: <FiUsers />,
        allowed: canViewAdmins() && user?.role !== 'lga-admin'
      },
      {
        name: "Chat",
        path: "/admin/create-group-chat",
        icon: <FiMessageSquare />,
        allowed: canCreateGroupChats()
      },
      {
        name: "Broadcast",
        path: "/admin/broadcast",
        icon: <FiSend />,
        allowed: canBroadcast()
      },
      {
        name: "Reports",
        path: "/admin/reports",
        icon: <FiBarChart2 />,
        allowed: canViewReports()
      },
      {
        name: "Create Custom Role",
        path: "/admin/create-custom-role",
        icon: <FiShield />,
        allowed: user?.role === 'super-admin'
      },
      // {
      //   name: "Brand Identity",
      //   path: "/admin/manage-brand",
      //   icon: <FiFlag />,
      //   allowed: user?.role === 'super-admin' && hasPermission("manage-branding")
      // },
    ];

    return items.filter(item => item.allowed);
  };

  const handleItemClick = (path) => {
    navigate(path);
    if (window.innerWidth < 768) {
      onClose();
    }
  };

  const menuItems = getMenuItems();

  // Format role display name
  const getRoleDisplayName = () => {
    if (!user?.role) return 'Administration';
    
    const roleMap = {
      'super-admin': 'SUPER ADMIN',
      'state-admin': 'STATE ADMIN',
      'sd-admin': 'SENATORIAL DISTRICT ADMIN',
      'fc-admin': 'FEDERAL CONSTITUENCY ADMIN',
      'lga-admin': 'LOCAL GOVERNMENT ADMIN',
      'lcda-admin': 'LCDA ADMIN',
      'ward-admin': 'WARD ADMIN',
      'polling-unit-admin': 'POLLING UNIT ADMIN'
    };
    
    return roleMap[user.role] || user.role.replace('-', ' ').toUpperCase();
  };

  // Inject isolated CSS
  React.useEffect(() => {
    const styleId = 'admin-sidebar-isolated-styles';
    if (!document.getElementById(styleId)) {
      const styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.innerHTML = `
        /* === ADMIN SIDEBAR ISOLATED STYLES === */
        /* This CSS is completely isolated and will not affect other components */
        
        .admin-sidebar-isolated-wrapper {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 280px !important;
          height: 100vh !important;
          z-index: 9999 !important;
          transform: translateX(-100%);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          isolation: isolate !important;
          pointer-events: none !important;
        }

        .admin-sidebar-isolated-wrapper.open {
          transform: translateX(0) !important;
          pointer-events: all !important;
        }

        .admin-sidebar-isolated-container {
          width: 100% !important;
          height: 100% !important;
          background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%) !important;
          border-right: 1px solid #334155 !important;
          display: flex !important;
          flex-direction: column !important;
          overflow: hidden !important;
          position: relative !important;
        }

        /* Overlay - covers everything */
        .admin-sidebar-overlay-isolated {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          background: rgba(0, 0, 0, 0.8) !important;
          z-index: 9998 !important;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.3s ease, visibility 0.3s ease;
          backdrop-filter: blur(4px) !important;
          -webkit-backdrop-filter: blur(4px) !important;
          pointer-events: none !important;
        }

        .admin-sidebar-overlay-isolated.active {
          opacity: 1 !important;
          visibility: visible !important;
          pointer-events: all !important;
        }

        /* Header */
        .admin-sidebar-header-isolated {
          padding: 24px 20px !important;
          background: rgba(15, 23, 42, 0.95) !important;
          border-bottom: 1px solid #334155 !important;
          flex-shrink: 0 !important;
        }

        .admin-sidebar-header-isolated h1 {
          color: #ffffff !important;
          font-size: 20px !important;
          font-weight: 700 !important;
          margin: 0 0 8px 0 !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        }

        .admin-sidebar-role-badge-isolated {
          display: inline-block !important;
          background: linear-gradient(45deg, #f59e0b, #d97706) !important;
          color: white !important;
          padding: 4px 12px !important;
          border-radius: 20px !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        }

        .admin-sidebar-role-badge-isolated.super-admin {
          background: linear-gradient(45deg, #ec4899, #db2777) !important;
        }

        .admin-sidebar-role-badge-isolated.state-admin {
          background: linear-gradient(45deg, #10b981, #059669) !important;
        }

        .admin-sidebar-role-badge-isolated.sd-admin {
          background: linear-gradient(45deg, #3b82f6, #2563eb) !important;
        }

        .admin-sidebar-role-badge-isolated.fc-admin {
          background: linear-gradient(45deg, #8b5cf6, #7c3aed) !important;
        }

        .admin-sidebar-role-badge-isolated.lga-admin {
          background: linear-gradient(45deg, #f97316, #ea580c) !important;
        }

        .admin-sidebar-role-badge-isolated.lcda-admin {
          background: linear-gradient(45deg, #14b8a6, #0d9488) !important;
        }

        .admin-sidebar-role-badge-isolated.ward-admin {
          background: linear-gradient(45deg, #8b5cf6, #7c3aed) !important;
        }

        .admin-sidebar-role-badge-isolated.polling-unit-admin {
          background: linear-gradient(45deg, #84cc16, #65a30d) !important;
        }

        /* Navigation */
        .admin-sidebar-nav-isolated {
          flex: 1 !important;
          overflow-y: auto !important;
          padding: 16px 0 !important;
        }

        .admin-sidebar-item-isolated {
          display: flex !important;
          align-items: center !important;
          gap: 12px !important;
          width: 100% !important;
          padding: 14px 20px !important;
          background: transparent !important;
          border: none !important;
          color: #cbd5e1 !important;
          font-size: 14px !important;
          font-weight: 500 !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          text-align: left !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
          margin: 0 !important;
        }

        .admin-sidebar-item-isolated:hover {
          background: rgba(255, 255, 255, 0.05) !important;
          color: #ffffff !important;
        }

        .admin-sidebar-item-isolated.active {
          background: rgba(59, 130, 246, 0.15) !important;
          color: #ffffff !important;
          font-weight: 600 !important;
          border-right: 3px solid #3b82f6 !important;
        }

        .admin-sidebar-item-icon-isolated {
          font-size: 16px !important;
          flex-shrink: 0 !important;
          width: 20px !important;
          text-align: center !important;
        }

        .admin-sidebar-item-isolated.active .admin-sidebar-item-icon-isolated {
          color: #3b82f6 !important;
        }

        /* Close button */
        .admin-sidebar-close-btn-isolated {
          position: absolute !important;
          top: 20px !important;
          right: 20px !important;
          width: 32px !important;
          height: 32px !important;
          border-radius: 50% !important;
          background: rgba(255, 255, 255, 0.1) !important;
          border: none !important;
          color: white !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          cursor: pointer !important;
          z-index: 10000 !important;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.2s ease, visibility 0.2s ease;
        }

        .admin-sidebar-close-btn-isolated:hover {
          background: rgba(255, 255, 255, 0.2) !important;
        }

        /* Empty state */
        .admin-sidebar-empty-isolated {
          padding: 40px 20px !important;
          text-align: center !important;
          color: #64748b !important;
        }

        .admin-sidebar-empty-isolated p {
          margin: 0 0 8px 0 !important;
          font-size: 14px !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        }

        .admin-sidebar-empty-isolated small {
          font-size: 12px !important;
          color: #94a3b8 !important;
        }

        /* Scrollbar */
        .admin-sidebar-nav-isolated::-webkit-scrollbar {
          width: 4px !important;
        }

        .admin-sidebar-nav-isolated::-webkit-scrollbar-track {
          background: transparent !important;
        }

        .admin-sidebar-nav-isolated::-webkit-scrollbar-thumb {
          background: #475569 !important;
          border-radius: 2px !important;
        }

        .admin-sidebar-nav-isolated::-webkit-scrollbar-thumb:hover {
          background: #64748b !important;
        }

        /* Responsive */
        @media (max-width: 767px) {
          .admin-sidebar-isolated-wrapper {
            width: 85% !important;
            max-width: 300px !important;
            box-shadow: 10px 0 30px rgba(0, 0, 0, 0.5) !important;
          }

          .admin-sidebar-close-btn-isolated {
            opacity: 1 !important;
            visibility: visible !important;
          }

          .admin-sidebar-overlay-isolated {
            display: block !important;
          }
        }

        @media (min-width: 768px) {
          .admin-sidebar-isolated-wrapper {
            transform: translateX(0) !important;
            pointer-events: all !important;
          }

          .admin-sidebar-overlay-isolated {
            display: none !important;
          }
        }

        /* === END ADMIN SIDEBAR STYLES === */
      `;
      document.head.appendChild(styleElement);
    }

    return () => {
      // Clean up on unmount if needed
      const styleElement = document.getElementById(styleId);
      if (styleElement && !document.querySelector('.admin-sidebar-isolated-wrapper')) {
        styleElement.remove();
      }
    };
  }, []);

  return (
    <>
      {/* Overlay - Will cover EVERYTHING else on mobile */}
      <div 
        className={`admin-sidebar-overlay-isolated ${isOpen ? 'active' : ''}`}
        onClick={onClose}
        onTouchStart={onClose}
      />

      {/* Sidebar Container */}
      <div className={`admin-sidebar-isolated-wrapper ${isOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-isolated-container">
          {/* Close button for mobile */}
          <button 
            className="admin-sidebar-close-btn-isolated"
            onClick={onClose}
            aria-label="Close sidebar"
          >
            <FiX size={18} />
          </button>

          {/* Header */}
          <div className="admin-sidebar-header-isolated">
            <h1>Admin Portal</h1>
            <span className={`admin-sidebar-role-badge-isolated ${user?.role?.replace(/-/g, '-')}`}>
              {getRoleDisplayName()}
            </span>
          </div>

          {/* Navigation */}
          <nav className="admin-sidebar-nav-isolated">
            {menuItems.length > 0 ? (
              menuItems.map((item, index) => (
                <button
                  key={index}
                  className={`admin-sidebar-item-isolated ${isActivePath(item.path) ? 'active' : ''}`}
                  onClick={() => handleItemClick(item.path)}
                >
                  <div className="admin-sidebar-item-icon-isolated">
                    {item.icon}
                  </div>
                  <span>{item.name}</span>
                </button>
              ))
            ) : (
              <div className="admin-sidebar-empty-isolated">
                <p>No menu items available</p>
                <small>Contact system administrator</small>
              </div>
            )}
          </nav>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;